<template>
  <el-popover
    placement="bottom"
    :width="228"
    trigger="click"
    popper-class="rb-style-popover"
  >
    <template #reference>
      <!-- По умолчанию триггер — цветной квадратик; заголовку колонки нужен свой -->
      <slot name="reference">
        <div
          class="series-color-indicator cursor-pointer"
          :class="{ 'series-color-indicator--sm': small }"
          :style="{ backgroundColor: color }"
        ></div>
      </slot>
    </template>

    <div class="style-picker">
      <!-- Подпись: своё название серии или значения разбивки («Мужской» вместо «1») -->
      <div v-if="name !== null" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleLabel') }}</div>
        <el-input
          :model-value="name"
          size="small"
          :placeholder="namePlaceholder"
          @update:model-value="$emit('update', { label: $event })"
        />
      </div>

      <!-- Color -->
      <div v-if="showColor" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleColor') }}</div>
        <div class="color-palette-grid">
          <div
            v-for="c in palette"
            :key="c"
            class="color-option"
            :class="{ active: color === c }"
            :style="{ backgroundColor: c }"
            @click="$emit('update', { color: c })"
          ></div>
        </div>
      </div>

      <!-- Chart type: only shown for reports where the trend as a whole is line or bar,
           lets a single series break out of the report-wide display and render as the
           other type (e.g. one bar among a bunch of lines). -->
      <div v-if="showChartType" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleChartType') }}</div>
        <div class="style-btn-row">
          <button
            class="style-btn"
            :class="{ active: chartType === 'line' }"
            @click="$emit('update', { chartType: 'line' })"
            :title="$t('ReportBuilder.table.styleChartTypeLine')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <polyline points="3,15 12,5 24,15 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: chartType === 'bar' }"
            @click="$emit('update', { chartType: 'bar' })"
            :title="$t('ReportBuilder.table.styleChartTypeBar')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <rect x="4" y="10" width="6" height="7" fill="currentColor"/>
              <rect x="15" y="4" width="6" height="13" fill="currentColor"/>
              <rect x="26" y="8" width="6" height="9" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Line width -->
      <div v-if="!colorOnly" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleWidth') }}</div>
        <div class="style-btn-row">
          <button
            v-for="w in lineWidths"
            :key="w.value"
            class="style-btn"
            :class="{ active: lineWidth === w.value }"
            @click="$emit('update', { lineWidth: w.value })"
            :title="w.label"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" :stroke-width="w.value" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Line style -->
      <div v-if="!colorOnly" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleType') }}</div>
        <div class="style-btn-row">
          <button
            class="style-btn"
            :class="{ active: lineStyle === 'solid' }"
            @click="$emit('update', { lineStyle: 'solid' })"
            :title="$t('ReportBuilder.table.styleSolid')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: lineStyle === 'dashed' }"
            @click="$emit('update', { lineStyle: 'dashed' })"
            :title="$t('ReportBuilder.table.styleDashed')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 4"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: lineStyle === 'dotted' }"
            @click="$emit('update', { lineStyle: 'dotted' })"
            :title="$t('ReportBuilder.table.styleDotted')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="2 4"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: lineStyle === 'dashdot' }"
            @click="$emit('update', { lineStyle: 'dashdot' })"
            :title="$t('ReportBuilder.table.styleDashdot')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 3 2 3"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Tension -->
      <div v-if="!colorOnly" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleTension') }}</div>
        <div class="style-btn-row">
          <button
            class="style-btn"
            :class="{ active: lineTension === 'straight' }"
            @click="$emit('update', { lineTension: 'straight' })"
            :title="$t('ReportBuilder.table.styleStraight')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <polyline points="3,15 12,5 24,15 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: lineTension === 'smooth' }"
            @click="$emit('update', { lineTension: 'smooth' })"
            :title="$t('ReportBuilder.table.styleSmooth')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <path d="M3,15 C9,15 9,5 18,10 C27,15 27,5 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Points -->
      <div v-if="!colorOnly" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.stylePoints') }}</div>
        <div class="style-btn-row">
          <button
            class="style-btn"
            :class="{ active: !showPoints }"
            @click="$emit('update', { showPoints: false })"
            :title="$t('ReportBuilder.table.styleNoPoints')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: showPoints }"
            @click="$emit('update', { showPoints: true })"
            :title="$t('ReportBuilder.table.styleWithPoints')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="10" cy="10" r="2.5" fill="currentColor"/>
              <circle cx="18" cy="10" r="2.5" fill="currentColor"/>
              <circle cx="26" cy="10" r="2.5" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Fill -->
      <div v-if="!colorOnly" class="style-section">
        <div class="style-label">{{ $t('ReportBuilder.table.styleFill') }}</div>
        <div class="style-btn-row">
          <button
            class="style-btn"
            :class="{ active: !fill }"
            @click="$emit('update', { fill: false })"
            :title="$t('ReportBuilder.table.styleFillOff')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <polyline points="3,15 12,8 22,12 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
            class="style-btn"
            :class="{ active: fill }"
            @click="$emit('update', { fill: true })"
            :title="$t('ReportBuilder.table.styleFillOn')"
          >
            <svg width="36" height="20" viewBox="0 0 36 20">
              <polyline points="3,15 12,8 22,12 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <polygon points="3,15 12,8 22,12 33,5 33,17 3,17" fill="currentColor" opacity="0.25"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="resettable" class="style-section style-reset">
        <el-button size="small" text type="info" @click="$emit('update', { color: null, label: null })">
          {{ $t('ReportBuilder.table.styleReset') }}
        </el-button>
      </div>
    </div>
  </el-popover>
</template>

<script>
export default {
  name: 'SeriesColorPicker',
  props: {
    color: { type: String, default: '#909399' },
    lineWidth: { type: Number, default: 1 },
    lineStyle: { type: String, default: 'solid' },
    lineTension: { type: String, default: 'smooth' },
    showPoints: { type: Boolean, default: false },
    fill: { type: Boolean, default: false },
    palette: { type: Array, required: true },
    lineWidths: { type: Array, default: () => [] },
    // Smaller swatch for the inline (dashboard-widget) placement next to the series name
    small: { type: Boolean, default: false },
    // Круговая/столбцы/KPI и значения разбивки: настройки линии к ним неприменимы
    colorOnly: { type: Boolean, default: false },
    // Цвет серии не действует, когда серии разложены по значениям разбивки —
    // тогда в попапе остаётся только подпись, чтобы не предлагать неработающую настройку
    showColor: { type: Boolean, default: true },
    // Не null — показывать поле подписи (своё название серии или значения разбивки)
    name: { type: String, default: null },
    namePlaceholder: { type: String, default: '' },
    // Индивидуальный тип отображения серии (line/bar), independent от общего type_chart отчёта
    chartType: { type: String, default: 'line' },
    // Показывать переключатель типа отображения — только когда отчёт сам line/bar
    showChartType: { type: Boolean, default: false },
    // Показывать «Сбросить» — для оформления, которое можно вернуть к значению по умолчанию
    resettable: { type: Boolean, default: false }
  },
  emits: ['update']
}
</script>

<style scoped>
.series-color-indicator {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.series-color-indicator:hover {
  transform: scale(1.1);
}

.series-color-indicator--sm {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  box-shadow: none;
}

.style-picker {
  padding: 2px 0;
}

.style-section {
  margin-bottom: 12px;
}

.style-section:last-child {
  margin-bottom: 0;
}

.style-reset {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid var(--ui-border, #ebeef5);
  padding-top: 6px;
}

.style-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--ui-text-3, #909399);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.color-palette-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.color-option {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: #303133;
  box-shadow: 0 0 4px rgba(0,0,0,0.2);
}

.style-btn-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.style-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 32px;
  border: 1.5px solid var(--ui-border, #dcdfe6);
  border-radius: 6px;
  background: var(--ui-bg-card, #fff);
  cursor: pointer;
  color: var(--ui-text-2, #606266);
  padding: 0;
  transition: all 0.15s;
}

.style-btn:hover {
  border-color: #409EFF;
  color: #409EFF;
}

.style-btn.active {
  border-color: #409EFF;
  background: #ecf5ff;
  color: #409EFF;
}
</style>
